/**
 * useGeminiLive — React hook for real-time voice conversations via Gemini Live API
 *
 * Uses @google/genai SDK directly with ephemeral tokens (bypasses Firebase AI Logic proxy).
 *
 * Handles:
 * - Ephemeral token retrieval from backend
 * - Mic capture via AudioWorklet (PCM 16kHz mono)
 * - Audio playback (PCM 24kHz) with gapless scheduling
 * - Live transcript capture (input + output transcription)
 * - Conversation context injection from ongoing chat
 * - Interruption handling
 * - Cleanup on disconnect
 */

import { useState, useRef, useCallback } from 'react';
import { GoogleGenAI, Modality } from '@google/genai';
import type { Session } from '@google/genai';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase';

export interface TranscriptEntry {
  /** 'system' entries are diagnostic markers (pause boundaries, interruptions) — not speech content. */
  role: 'user' | 'assistant' | 'system';
  text: string;
  timestamp: string;
}

export type VoiceState = 'idle' | 'connecting' | 'active' | 'error';

export interface AudioDevice {
  deviceId: string;
  label: string;
}

export interface UseGeminiLiveReturn {
  state: VoiceState;
  connect: (deviceId?: string) => Promise<void>;
  disconnect: () => void;
  reconnect: () => void;
  setSilenceDuration: (ms: number) => void;
  transcript: TranscriptEntry[];
  error: string | null;
  micLevel: number;
  rawMicLevel: number;
  audioChunksSent: number;
  debugInfo: string;
  micDeviceName: string;
  availableDevices: AudioDevice[];
  refreshDevices: () => Promise<void>;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** Options for customizing the Gemini Live session (e.g., for interview voice mode). */
export interface GeminiLiveOptions {
  /** Cloud Function name to call for voice context. Default: 'getVoiceContext'. */
  contextFunction?: string;
  /** Extra params merged with { language } when calling the context function. */
  contextParams?: Record<string, any>;
  /** If provided, this prompt is sent as the initial turn instead of the default greeting. */
  initialPrompt?: string;
  /** When true, chat history injection is skipped (e.g., for interview mode). */
  skipChatHistory?: boolean;
}

// Inline AudioWorklet processor source
// Buffers resampled PCM samples and posts chunks of ~2048 samples (~128ms at 16kHz)
// Also reports RMS audio level for debugging
const WORKLET_SOURCE = `
class PCMProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    this.targetRate = (options.processorOptions && options.processorOptions.targetSampleRate) || 16000;
    this.bufferSize = (options.processorOptions && options.processorOptions.bufferSize) || 2048;
    this.buffer = new Int16Array(this.bufferSize);
    this.writePos = 0;
    this.chunkCount = 0;
  }
  process(inputs) {
    const input = inputs[0];
    if (!input || !input[0] || input[0].length === 0) return true;
    const float32 = input[0];
    const ratio = sampleRate / this.targetRate;
    const outLen = Math.floor(float32.length / ratio);
    for (let i = 0; i < outLen; i++) {
      const srcIdx = Math.floor(i * ratio);
      const s = Math.max(-1, Math.min(1, float32[srcIdx]));
      this.buffer[this.writePos++] = s < 0 ? s * 0x8000 : s * 0x7FFF;
      if (this.writePos >= this.bufferSize) {
        this.chunkCount++;
        const chunk = this.buffer.slice(0);
        // Calculate RMS on every chunk for live level meter
        let sumSq = 0;
        for (let j = 0; j < chunk.length; j++) {
          sumSq += chunk[j] * chunk[j];
        }
        const rms = Math.sqrt(sumSq / chunk.length);
        this.port.postMessage({ type: 'level', rms: rms, chunk: this.chunkCount });
        this.port.postMessage(chunk.buffer, [chunk.buffer]);
        this.buffer = new Int16Array(this.bufferSize);
        this.writePos = 0;
      }
    }
    return true;
  }
}
registerProcessor('pcm-processor', PCMProcessor);
`;

const SERVER_OUTPUT_SAMPLE_RATE = 24000;

interface VoiceContextResponse {
  systemPrompt: string;
  ephemeralToken: string;
  model: string;
}

export function useGeminiLive(
  language: string,
  chatHistory?: ChatMessage[],
  options?: GeminiLiveOptions,
): UseGeminiLiveReturn {
  const [state, setState] = useState<VoiceState>('idle');
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [micLevel, setMicLevel] = useState(0);
  const [rawMicLevel, setRawMicLevel] = useState(0);
  const [audioChunksSent, setAudioChunksSent] = useState(0);
  const [debugInfo, setDebugInfo] = useState('');
  const [micDeviceName, setMicDeviceName] = useState('');
  const [availableDevices, setAvailableDevices] = useState<AudioDevice[]>([]);

  const sessionRef = useRef<Session | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const analyserIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stoppedRef = useRef(false);

  // Session resumption state
  const resumptionHandleRef = useRef<string | null>(null);
  const reconnectingRef = useRef(false);
  const lastDeviceIdRef = useRef<string | undefined>(undefined);
  const voiceContextRef = useRef<VoiceContextResponse | null>(null);
  const connectRef = useRef<((deviceId?: string) => Promise<void>) | null>(null);

  // Pause detection sensitivity (configurable via UI). 800ms is conversationally natural;
  // 1500ms (old default) felt too long.
  const silenceDurationMsRef = useRef<number>(800);

  // Safety timeout ref — clears stuck 'connecting' state after 30s
  const connectingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Delayed-flush ref — gives late-arriving inputTranscription chunks time to land
  // before we flush the buffer. inputTranscription chunks routinely arrive AFTER turnComplete.
  const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Playback state
  const scheduledSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const nextStartTimeRef = useRef(0);

  // Transcript accumulation
  const inputChunksRef = useRef<string[]>([]);
  const outputChunksRef = useRef<string[]>([]);

  const flushInputTranscript = useCallback((markerAfter?: string) => {
    const text = inputChunksRef.current.join('').trim();
    if (text) {
      setTranscript(prev => [...prev, {
        role: 'user',
        text,
        timestamp: new Date().toISOString(),
      }]);
    }
    inputChunksRef.current = [];
    if (markerAfter) {
      setTranscript(prev => [...prev, { role: 'system', text: markerAfter, timestamp: new Date().toISOString() }]);
    }
  }, []);

  const flushOutputTranscript = useCallback(() => {
    const text = outputChunksRef.current.join('').trim();
    if (text) {
      setTranscript(prev => [...prev, {
        role: 'assistant',
        text,
        timestamp: new Date().toISOString(),
      }]);
    }
    outputChunksRef.current = [];
  }, []);

  const interruptPlayback = useCallback(() => {
    [...scheduledSourcesRef.current].forEach(source => {
      try { source.stop(0); } catch {}
    });
    scheduledSourcesRef.current = [];
    if (audioContextRef.current) {
      nextStartTimeRef.current = audioContextRef.current.currentTime;
    }
  }, []);

  const enqueueAndPlay = useCallback((pcmBuffer: ArrayBuffer) => {
    const ctx = audioContextRef.current;
    if (!ctx || stoppedRef.current) return;

    try {
      const pcm16 = new Int16Array(pcmBuffer);
      const audioBuffer = ctx.createBuffer(1, pcm16.length, SERVER_OUTPUT_SAMPLE_RATE);
      const channelData = audioBuffer.getChannelData(0);
      for (let i = 0; i < pcm16.length; i++) {
        channelData[i] = pcm16[i] / 32768;
      }

      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);

      scheduledSourcesRef.current.push(source);
      source.onended = () => {
        scheduledSourcesRef.current = scheduledSourcesRef.current.filter(s => s !== source);
      };

      nextStartTimeRef.current = Math.max(ctx.currentTime, nextStartTimeRef.current);
      source.start(nextStartTimeRef.current);
      nextStartTimeRef.current += audioBuffer.duration;
    } catch (e) {
      console.error('Audio playback error:', e);
    }
  }, []);

  const cleanupAudio = useCallback(() => {
    interruptPlayback();

    if (analyserIntervalRef.current) {
      clearInterval(analyserIntervalRef.current);
      analyserIntervalRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    if (workletNodeRef.current) {
      workletNodeRef.current.port.onmessage = null;
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  }, [interruptPlayback]);

  const refreshDevices = useCallback(async () => {
    try {
      // Need a temporary getUserMedia call to get device labels (browsers hide labels without permission)
      const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      tempStream.getTracks().forEach(t => t.stop());
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices
        .filter(d => d.kind === 'audioinput' && d.deviceId !== '')
        .map(d => ({ deviceId: d.deviceId, label: d.label || `Mic ${d.deviceId.substring(0, 8)}` }));
      setAvailableDevices(audioInputs);
    } catch (e) {
      console.error('[Voice] Failed to enumerate devices:', e);
    }
  }, []);

  const connect = useCallback(async (selectedDeviceId?: string) => {
    // Allow re-entry when reconnecting (state is already 'connecting' from onclose)
    if (state === 'active') return;
    if (state === 'connecting' && !reconnectingRef.current) return;

    setState('connecting');
    setError(null);
    lastDeviceIdRef.current = selectedDeviceId;
    const isResuming = !!resumptionHandleRef.current;

    // Safety net: if still connecting after 30s, transition to error instead of spinning forever
    if (connectingTimeoutRef.current) clearTimeout(connectingTimeoutRef.current);
    connectingTimeoutRef.current = setTimeout(() => {
      if (stoppedRef.current || sessionRef.current) return; // already succeeded or stopped
      console.warn('[Voice] Connection timeout after 30s — no session established.');
      setError('Connection timed out. Check your internet connection and try again.');
      setState('error');
      cleanupAudio();
    }, 30_000);

    // Only reset transcript on fresh connections (not resumes)
    if (!isResuming) {
      setTranscript([]);
      inputChunksRef.current = [];
      outputChunksRef.current = [];
    }
    stoppedRef.current = false;

    try {
      // 1. Fetch enriched system prompt + ephemeral token from backend
      // On resume, reuse cached context if available (token may still be valid)
      let data: VoiceContextResponse;
      if (isResuming && voiceContextRef.current) {
        data = voiceContextRef.current;
        console.log('[Voice] Step 1: Reusing cached voice context for resume.');
      } else {
        console.log('[Voice] Step 1: Fetching voice context + ephemeral token...');
        const contextFnName = options?.contextFunction || 'getVoiceContext';
        const contextFnParams = { language, ...(options?.contextParams || {}) };
        const getVoiceContext = httpsCallable<any, VoiceContextResponse>(
          functions, contextFnName
        );
        const result = await getVoiceContext(contextFnParams);
        data = result.data;
        voiceContextRef.current = data;
        console.log('[Voice] Step 1 complete. Prompt length:', data.systemPrompt?.length, 'Token received:', !!data.ephemeralToken);
      }

      if (!data.ephemeralToken) {
        throw new Error('No ephemeral token received from server');
      }

      // 2. Create Google GenAI client with ephemeral token (must use v1alpha for ephemeral tokens)
      console.log('[Voice] Step 2: Creating GenAI client with ephemeral token...');
      const client = new GoogleGenAI({
        apiKey: data.ephemeralToken,
        httpOptions: { apiVersion: 'v1alpha' },
      });

      // 3. Set up AudioContext (must be in response to user gesture)
      const audioContext = new AudioContext();
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      audioContextRef.current = audioContext;
      nextStartTimeRef.current = audioContext.currentTime;

      // 4. Enumerate available audio devices
      console.log('[Voice] === MIC DIAGNOSTICS ===');
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        console.log(`[Voice] Found ${audioInputs.length} audio input devices:`);
        audioInputs.forEach((d, i) => {
          console.log(`[Voice]   ${i}: "${d.label}" (id: ${d.deviceId.substring(0, 12)}...)`);
        });
        setDebugInfo(`Found ${audioInputs.length} mic devices`);
      } catch (e) {
        console.log('[Voice] Could not enumerate devices:', e);
      }

      // Get microphone access (use selected device if provided)
      // Use `ideal` for deviceId and processing constraints so the browser can relax
      // them if the hardware can't satisfy all at once (common on Mac with external devices).
      const audioConstraints: MediaTrackConstraints = {
        echoCancellation: { ideal: true },
        noiseSuppression: { ideal: true },
        autoGainControl: { ideal: true },
      };
      if (selectedDeviceId) {
        audioConstraints.deviceId = { ideal: selectedDeviceId };
        console.log('[Voice] Requesting specific device:', selectedDeviceId.substring(0, 20));
      }
      let mediaStream: MediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      } catch (devErr: any) {
        // If the preferred device fails, fall back to the system default mic
        console.warn('[Voice] getUserMedia failed with preferred device, falling back to default:', devErr.message);
        mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: { ideal: true }, noiseSuppression: { ideal: true }, autoGainControl: { ideal: true } },
        });
      }
      mediaStreamRef.current = mediaStream;

      // Detailed mic device diagnostics
      const audioTrack = mediaStream.getAudioTracks()[0];
      const trackSettings = audioTrack?.getSettings();
      const deviceLabel = audioTrack?.label || 'Unknown device';
      setMicDeviceName(deviceLabel);
      console.log('[Voice] === SELECTED MIC ===');
      console.log('[Voice] Device label:', deviceLabel);
      console.log('[Voice] Track enabled:', audioTrack?.enabled);
      console.log('[Voice] Track muted:', audioTrack?.muted);
      console.log('[Voice] Track readyState:', audioTrack?.readyState);
      console.log('[Voice] Sample rate:', trackSettings?.sampleRate);
      console.log('[Voice] Channel count:', (trackSettings as any)?.channelCount);
      console.log('[Voice] Device ID:', trackSettings?.deviceId?.substring(0, 20));
      console.log('[Voice] Echo cancellation:', trackSettings?.echoCancellation);
      console.log('[Voice] Noise suppression:', trackSettings?.noiseSuppression);
      console.log('[Voice] Auto gain:', trackSettings?.autoGainControl);
      console.log('[Voice] AudioContext state:', audioContext.state, 'sampleRate:', audioContext.sampleRate);

      // Set up independent AnalyserNode to verify raw mic input (bypasses worklet)
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyserRef.current = analyser;
      const analyserSource = audioContext.createMediaStreamSource(mediaStream);
      analyserSource.connect(analyser);

      const analyserData = new Uint8Array(analyser.frequencyBinCount);
      let analyserCheckCount = 0;
      analyserIntervalRef.current = setInterval(() => {
        if (stoppedRef.current) return;
        analyser.getByteTimeDomainData(analyserData);
        // Calculate RMS from time domain data (centered at 128)
        let sumSq = 0;
        let min = 255, max = 0;
        for (let i = 0; i < analyserData.length; i++) {
          const v = (analyserData[i] - 128) / 128;
          sumSq += v * v;
          if (analyserData[i] < min) min = analyserData[i];
          if (analyserData[i] > max) max = analyserData[i];
        }
        const rms = Math.sqrt(sumSq / analyserData.length);
        const normalized = Math.min(100, Math.round(rms * 300));
        setRawMicLevel(normalized);
        analyserCheckCount++;
        // Log first few checks and then every 50th
        if (analyserCheckCount <= 5 || analyserCheckCount % 50 === 0) {
          console.log(`[Voice] Raw analyser #${analyserCheckCount}: RMS=${(rms * 100).toFixed(1)}% normalized=${normalized} range=[${min}-${max}] track.readyState=${audioTrack?.readyState} track.muted=${audioTrack?.muted}`);
        }
      }, 100);

      // 5. Load AudioWorklet and set up audio pipeline
      const workletBlob = new Blob([WORKLET_SOURCE], { type: 'application/javascript' });
      const workletURL = URL.createObjectURL(workletBlob);
      await audioContext.audioWorklet.addModule(workletURL);
      URL.revokeObjectURL(workletURL);

      const sourceNode = audioContext.createMediaStreamSource(mediaStream);
      sourceNodeRef.current = sourceNode;

      const workletNode = new AudioWorkletNode(audioContext, 'pcm-processor', {
        processorOptions: { targetSampleRate: 16000 },
      });
      workletNodeRef.current = workletNode;

      // 6. Connect to Gemini Live API via @google/genai SDK
      const isResume = !!resumptionHandleRef.current;
      console.log('[Voice] Step 3: Connecting to Gemini Live API...', data.model, isResume ? '(resuming)' : '(new session)');
      const session = await client.live.connect({
        model: data.model,
        config: {
          responseModalities: [Modality.AUDIO],
          systemInstruction: data.systemPrompt,
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: {
                voiceName: 'Aoede',
              },
            },
          },
          inputAudioTranscription: {},
          outputAudioTranscription: {},
          // Enable context window compression for longer sessions
          contextWindowCompression: { slidingWindow: {} },
          // Enable session resumption so we can auto-reconnect on connection drops
          sessionResumption: resumptionHandleRef.current
            ? { handle: resumptionHandleRef.current }
            : {},
          // VAD: controls how long silence must last before Gemini considers the user done.
          // MUST be inside realtimeInputConfig.automaticActivityDetection — NOT top-level.
          // Confirmed supported by gemini-2.5-flash-native-audio-preview-12-2025.
          realtimeInputConfig: {
            automaticActivityDetection: {
              silenceDurationMs: silenceDurationMsRef.current,
            },
          },
        },
        callbacks: {
          onopen: () => {
            console.log('[Voice] WebSocket connected.');
          },
          onmessage: (message: any) => {
            if (stoppedRef.current) return;

            // Handle session resumption updates — store the latest handle for auto-reconnect
            if (message.sessionResumptionUpdate) {
              const update = message.sessionResumptionUpdate;
              if (update.newHandle) {
                resumptionHandleRef.current = update.newHandle;
                console.log('[Voice] Session resumption handle updated, resumable:', update.resumable);
              }
            }

            // Debug: log message types (reduce noise after initial debug)
            const keys = Object.keys(message).filter(k => message[k] != null);
            if (!keys.includes('serverContent') || message.serverContent?.turnComplete || message.serverContent?.interrupted) {
              console.log('[Voice] Message:', keys);
            }
            if (message.serverContent) {
              const scKeys = Object.keys(message.serverContent).filter(k => (message.serverContent as any)[k] != null);
              if (scKeys.includes('inputTranscription') || scKeys.includes('outputTranscription') || scKeys.includes('turnComplete')) {
                console.log('[Voice] serverContent:', scKeys);
              }
            }

            // Handle interruption (user spoke while AI was responding)
            if (message.serverContent?.interrupted) {
              interruptPlayback();
              flushOutputTranscript();
              // Cancel any pending delayed flush and flush immediately
              if (flushTimeoutRef.current) { clearTimeout(flushTimeoutRef.current); flushTimeoutRef.current = null; }
              flushInputTranscript('⏸ interrupted');
            }

            // Handle audio playback - try both the parts approach and the data getter
            const audioPart = message.serverContent?.modelTurn?.parts?.find(
              (part: any) => part.inlineData?.mimeType?.startsWith('audio/')
            );
            if (audioPart?.inlineData) {
              const binary = atob(audioPart.inlineData.data);
              const bytes = new Uint8Array(binary.length);
              for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
              }
              enqueueAndPlay(bytes.buffer);
            } else if (message.data) {
              // Fallback: use the data getter which returns concatenated inline data
              const binary = atob(message.data);
              const bytes = new Uint8Array(binary.length);
              for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
              }
              enqueueAndPlay(bytes.buffer);
            }

            // Handle input transcription
            if (message.serverContent?.inputTranscription?.text) {
              inputChunksRef.current.push(message.serverContent.inputTranscription.text);
            }

            // Handle output transcription
            if (message.serverContent?.outputTranscription?.text) {
              outputChunksRef.current.push(message.serverContent.outputTranscription.text);
            }

            // On turn complete, delay the input transcript flush by 400ms.
            // inputTranscription chunks routinely arrive in later WebSocket messages
            // AFTER the turnComplete message — an immediate flush drops them.
            // The 400ms window captures stragglers without noticeable UX lag.
            if (message.serverContent?.turnComplete) {
              // Output transcript is complete at this point — flush immediately.
              flushOutputTranscript();
              // Input transcript: cancel any pending flush and reschedule.
              if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current);
              flushTimeoutRef.current = setTimeout(() => {
                flushTimeoutRef.current = null;
                flushInputTranscript('⏸ pause');
              }, 400);
            }
          },
          onerror: (e: any) => {
            if (!stoppedRef.current) {
              console.error('[Voice] Session error:', e);
              if (flushTimeoutRef.current) { clearTimeout(flushTimeoutRef.current); flushTimeoutRef.current = null; }
              flushInputTranscript();
              flushOutputTranscript();
              cleanupAudio();
              sessionRef.current = null;
              setError('Voice session encountered an error. You can retry.');
              setState('error');
            }
          },
          onclose: (e: any) => {
            // Log all available close event details for debugging
            console.log('[Voice] Session closed. Details:', JSON.stringify({
              reason: e?.reason,
              code: e?.code,
              type: e?.type,
              wasClean: e?.wasClean,
              keys: e ? Object.keys(e) : 'null',
            }));
            if (!stoppedRef.current) {
              // If we have a resumption handle, try to auto-reconnect
              if (resumptionHandleRef.current && !reconnectingRef.current) {
                console.log('[Voice] Connection dropped, attempting auto-reconnect with resumption handle...');
                reconnectingRef.current = true;
                sessionRef.current = null;
                // Clean up old audio pipeline but keep transcript
                cleanupAudio();
                setState('connecting');
                setDebugInfo('Reconnecting...');
                // Short delay then reconnect using ref to avoid stale closure.
                // IMPORTANT: reset reconnectingRef AFTER calling connect, not before.
                // connect() checks: state==='connecting' && !reconnectingRef → early return.
                // If we reset before calling connect, that guard triggers and we spin forever.
                setTimeout(() => {
                  if (!stoppedRef.current && connectRef.current) {
                    connectRef.current(lastDeviceIdRef.current);
                  }
                  reconnectingRef.current = false;
                }, 1500);
              } else {
                // No resumption handle or already trying — show error
                stoppedRef.current = true;
                flushInputTranscript();
                flushOutputTranscript();
                cleanupAudio();
                sessionRef.current = null;
                setError('Voice session ended. Click retry to start a new session.');
                setState('error');
              }
            }
          },
        },
      });

      console.log('[Voice] Step 3 complete. Session connected.');
      sessionRef.current = session;
      setDebugInfo('Session connected. Injecting context...');

      // 7. Inject conversation context (skip on resume — context is preserved)
      if (isResume) {
        console.log('[Voice] Resumed session — skipping context injection.');
      } else if (options?.initialPrompt) {
        // Custom initial prompt (e.g., interview mode)
        try {
          session.sendClientContent({
            turns: [{
              role: 'user',
              parts: [{ text: options.initialPrompt }],
            }],
            turnComplete: true,
          });
          console.log('[Voice] Sent custom initial prompt.');
        } catch (e) {
          console.error('[Voice] Failed to send custom initial prompt:', e);
        }
      } else if (!options?.skipChatHistory && chatHistory && chatHistory.length > 0) {
        try {
          // Send recent chat history so the voice model has context
          // Limit to last 10 messages to avoid exceeding context
          const recentHistory = chatHistory.slice(-10);
          const turns = recentHistory.map(msg => ({
            role: msg.role === 'assistant' ? 'model' : 'user',
            parts: [{ text: msg.content }],
          }));
          session.sendClientContent({ turns, turnComplete: false });
          console.log('[Voice] Injected', recentHistory.length, 'messages of chat context.');

          // Now send a transition message to start the voice conversation
          session.sendClientContent({
            turns: [{
              role: 'user',
              parts: [{ text: language === 'fr'
                ? '[L\'utilisateur passe maintenant en mode vocal. Continuez la conversation naturellement à voix haute. Saluez-le brièvement et demandez comment il va.]'
                : '[The user is now switching to voice mode. Continue the conversation naturally out loud. Greet them briefly and ask how they are doing.]'
              }],
            }],
            turnComplete: true,
          });
          console.log('[Voice] Sent voice transition prompt.');
        } catch (e) {
          console.error('[Voice] Failed to inject chat context:', e);
        }
      } else {
        // No chat history - start fresh voice conversation
        try {
          session.sendClientContent({
            turns: [{
              role: 'user',
              parts: [{ text: language === 'fr'
                ? '[Début d\'une conversation vocale. Saluez l\'utilisateur brièvement et demandez comment sa semaine se passe.]'
                : '[Starting a voice conversation. Greet the user briefly and ask how their week is going.]'
              }],
            }],
            turnComplete: true,
          });
          console.log('[Voice] Sent initial greeting prompt.');
        } catch (e) {
          console.error('[Voice] Failed to send initial prompt:', e);
        }
      }

      // 8. Send mic audio to Gemini
      let audioChunkCount = 0;
      workletNode.port.onmessage = (event) => {
        if (stoppedRef.current || !sessionRef.current) return;

        // Handle level reports from worklet
        if (event.data && event.data.type === 'level') {
          const normalized = Math.min(100, Math.round((event.data.rms / 3000) * 100));
          setMicLevel(normalized);
          return;
        }

        const pcm16 = event.data;
        const base64 = btoa(
          String.fromCharCode.apply(null, Array.from(new Uint8Array(pcm16)))
        );
        audioChunkCount++;
        setAudioChunksSent(audioChunkCount);
        // Only log first chunk to reduce noise
        if (audioChunkCount === 1) {
          console.log(`[Voice] First audio chunk sent, size: ${base64.length} bytes`);
        }
        try {
          sessionRef.current.sendRealtimeInput({
            audio: {
              data: base64,
              mimeType: 'audio/pcm;rate=16000',
            },
          });
        } catch (e) {
          console.error('[Voice] Error sending audio:', e);
        }
      };

      sourceNode.connect(workletNode);
      workletNode.connect(audioContext.destination);

      if (connectingTimeoutRef.current) {
        clearTimeout(connectingTimeoutRef.current);
        connectingTimeoutRef.current = null;
      }
      setDebugInfo(`Streaming: ${audioContext.sampleRate}Hz→16kHz | Device: ${deviceLabel.substring(0, 40)}`);
      setState('active');

    } catch (err: any) {
      console.error('Voice connection error:', err);

      if (connectingTimeoutRef.current) {
        clearTimeout(connectingTimeoutRef.current);
        connectingTimeoutRef.current = null;
      }

      if (err.name === 'NotAllowedError') {
        setError('Microphone access was denied');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone found');
      } else {
        setError(err.message || 'Failed to connect');
      }

      setState('error');
      cleanupAudio();
      if (sessionRef.current) {
        try { sessionRef.current.close(); } catch {}
        sessionRef.current = null;
      }
    }
  }, [state, language, chatHistory, options, cleanupAudio, interruptPlayback, enqueueAndPlay, flushInputTranscript, flushOutputTranscript]);

  // Keep ref in sync so onclose handler always calls the latest connect
  connectRef.current = connect;

  const setSilenceDuration = useCallback((ms: number) => {
    silenceDurationMsRef.current = ms;
  }, []);

  const reconnect = useCallback(() => {
    if (state !== 'active' || !sessionRef.current) return;
    // Close session — onclose handler will auto-reconnect via resumption handle
    console.log('[Voice] Reconnecting with updated silence duration:', silenceDurationMsRef.current, 'ms');
    try { sessionRef.current.close(); } catch {}
  }, [state]);

  const disconnect = useCallback(async () => {
    if (stoppedRef.current) return;
    stoppedRef.current = true;

    // Clear resumption state — user intentionally ended the session
    resumptionHandleRef.current = null;
    voiceContextRef.current = null;
    reconnectingRef.current = false;

    // Cancel any pending delayed flush, then flush remaining transcripts
    if (flushTimeoutRef.current) { clearTimeout(flushTimeoutRef.current); flushTimeoutRef.current = null; }
    flushInputTranscript();
    flushOutputTranscript();

    cleanupAudio();

    if (sessionRef.current) {
      try {
        sessionRef.current.close();
      } catch {}
      sessionRef.current = null;
    }

    setState('idle');
  }, [cleanupAudio, flushInputTranscript, flushOutputTranscript]);

  return { state, connect, disconnect, reconnect, setSilenceDuration, transcript, error, micLevel, rawMicLevel, audioChunksSent, debugInfo, micDeviceName, availableDevices, refreshDevices };
}
